import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import TournamentSelector from '../components/TournamentSelector';
import { 
  ClipboardCheck, 
  Plus, 
  Search, 
  Trash2, 
  IndianRupee,
  FileText,
  Clock,
  CheckCircle,
  Wallet
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: FileText },
  pending_approval: { label: 'Pending Approval', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  paid: { label: 'Paid', color: 'bg-blue-100 text-blue-800', icon: Wallet },
};

const Approvals = () => {
  const { user, hasTabAccess } = useAuth();
  const navigate = useNavigate();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTournament, setSelectedTournament] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newApprovalName, setNewApprovalName] = useState('');
  const [newApprovalTournament, setNewApprovalTournament] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!selectedTournament) return;
    fetchApprovals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTournament]);

  const fetchApprovals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedTournament && selectedTournament !== 'all') {
        params.append('fiscal_year', selectedTournament);
      }
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const response = await axios.get(`${API}/approvals?${params.toString()}`, {
        withCredentials: true,
      });
      setApprovals(response.data);
    } catch (error) {
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApproval = async () => {
    if (!newApprovalName.trim()) {
      toast.error('Please enter an approval name');
      return;
    }
    if (!newApprovalTournament) {
      toast.error('Please select a tournament');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/approvals`, {
        name: newApprovalName,
        fiscal_year: newApprovalTournament,
      }, {
        withCredentials: true,
      });

      toast.success('Approval created successfully');
      setDialogOpen(false);
      setNewApprovalName('');

      // Navigate to the new approval detail page
      navigate(`/approvals/${response.data.approval_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create approval');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteApproval = async (approvalId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this approval?')) {
      return;
    }

    try {
      await axios.delete(`${API}/approvals/${approvalId}`, { withCredentials: true });
      toast.success('Approval deleted');
      fetchApprovals();
    } catch (error) {
      toast.error('Failed to delete approval');
    }
  };

  // Filter approvals by search term
  const filteredApprovals = approvals.filter(approval => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      approval.name?.toLowerCase().includes(term) ||
      approval.status?.toLowerCase().includes(term)
    );
  });

  // Calculate summary metrics
  const summaryMetrics = {
    total: approvals.length,
    draft: approvals.filter(a => a.status === 'draft').length,
    pending: approvals.filter(a => a.status === 'pending_approval').length,
    approved: approvals.filter(a => a.status === 'approved').length,
    paid: approvals.filter(a => a.status === 'paid').length,
    totalAmount: approvals.reduce((sum, a) => sum + (a.total_amount || 0), 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="approvals-page" className="space-y-8 min-w-0">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">Approvals &amp; Payments</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">Manage payment approvals and track vendor payments</p>
        </div>
        <div className="flex gap-3 flex-wrap items-end">
          {/* Search Input */}
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search approvals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-full"
              data-testid="search-approval-input"
            />
          </div>
          {/* Date Filters */}
          <div className="flex gap-2 items-center">
            <Input
              type="date"
              placeholder="From"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                fetchApprovals();
              }}
              className="w-36"
              data-testid="date-from-input"
            />
            <span className="text-muted-foreground">to</span>
            <Input
              type="date"
              placeholder="To"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                fetchApprovals();
              }}
              className="w-36"
              data-testid="date-to-input"
            />
          </div>
          {/* Tournament Filter */}
          <div className="w-44">
            <TournamentSelector
              value={selectedTournament}
              onChange={setSelectedTournament}
              showAllOption={true}
            />
          </div>
          
          {hasTabAccess('approvals') && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-approval-button" data-write-only>
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Approval
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading">Create New Approval</DialogTitle>
                  <DialogDescription>
                    Enter a name for the new approval batch
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="approval_name">Approval Name *</Label>
                    <Input
                      id="approval_name"
                      placeholder="e.g., February 2025 Vendor Payments"
                      value={newApprovalName}
                      onChange={(e) => setNewApprovalName(e.target.value)}
                      data-testid="approval-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="approval_tournament">Tournament *</Label>
                    <TournamentSelector
                      value={newApprovalTournament}
                      onChange={setNewApprovalTournament}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateApproval} disabled={creating}>
                    {creating ? 'Creating...' : 'Create & Continue'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
        <Card className="border-border bg-gradient-to-br from-blue-500/10 to-blue-600/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-muted-foreground leading-tight">Total Approvals</p>
                <p className="text-2xl sm:text-3xl font-bold">{summaryMetrics.total}</p>
              </div>
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full flex-shrink-0 bg-blue-500/20 flex items-center justify-center">
                <ClipboardCheck className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-gradient-to-br from-gray-500/10 to-gray-600/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-muted-foreground leading-tight">Draft</p>
                <p className="text-2xl sm:text-3xl font-bold">{summaryMetrics.draft}</p>
              </div>
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full flex-shrink-0 bg-gray-500/20 flex items-center justify-center">
                <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-gradient-to-br from-yellow-500/10 to-yellow-600/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-muted-foreground leading-tight">Pending</p>
                <p className="text-2xl sm:text-3xl font-bold">{summaryMetrics.pending}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-gradient-to-br from-green-500/10 to-green-600/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-muted-foreground leading-tight">Approved</p>
                <p className="text-2xl sm:text-3xl font-bold">{summaryMetrics.approved}</p>
              </div>
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full flex-shrink-0 bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-gradient-to-br from-emerald-500/10 to-emerald-600/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-muted-foreground leading-tight">Total Amount</p>
                <p className="text-lg sm:text-2xl font-bold text-emerald-600 break-words">
                  ₹{summaryMetrics.totalAmount.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full flex-shrink-0 bg-emerald-500/20 flex items-center justify-center">
                <IndianRupee className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Approvals List */}
      <Card className="border-border">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading">All Approvals</CardTitle>
          <CardDescription>
            {searchTerm ? `Showing ${filteredApprovals.length} of ${approvals.length} approvals` : `Total: ${approvals.length} approvals`}
            {selectedTournament && selectedTournament !== 'all' && ` · ${selectedTournament}`}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {filteredApprovals.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-muted-foreground">{searchTerm ? 'No matching approvals found' : 'No approvals found'}</p>
              <p className="text-sm text-muted-foreground mt-1">
                Click &quot;Create New Approval&quot; to get started
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-muted/50 text-muted-foreground uppercase text-xs tracking-wider font-semibold">
                    <th className="text-left py-3 px-4">Name</th>
                    <th className="text-left py-3 px-4">Tournament</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-right py-3 px-4">Invoices</th>
                    <th className="text-right py-3 px-4">Total Amount</th>
                    <th className="text-left py-3 px-4">Created</th>
                    {hasTabAccess('approvals') && <th className="text-center py-3 px-4">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredApprovals.map((approval) => {
                    const statusInfo = statusConfig[approval.status] || statusConfig.draft;
                    const StatusIcon = statusInfo.icon;
                    return (
                      <tr
                        key={approval.id}
                        data-testid={`approval-row-${approval.id}`}
                        className="border-b border-border hover:bg-muted/30 transition-colors cursor-pointer"
                        onClick={() => navigate(`/approvals/${approval.id}`)}
                      >
                        <td className="py-3 px-4">
                          <span className="font-medium text-primary hover:underline">{approval.name}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm">{approval.fiscal_year}</span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={`${statusInfo.color} flex items-center gap-1 w-fit`}>
                            <StatusIcon className="h-3 w-3" />
                            {statusInfo.label}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          {approval.invoices?.length || 0}
                        </td>
                        <td className="py-3 px-4 text-right font-mono">
                          ₹{(approval.total_amount || 0).toLocaleString('en-IN')}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {new Date(approval.created_at).toLocaleDateString('en-IN')}
                        </td>
                        {hasTabAccess('approvals') && (
                          <td className="py-3 px-4 text-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => handleDeleteApproval(approval.id, e)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-approval-${approval.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Approvals;
